import React, { useState, useEffect } from 'react';
import {
  GraduationCap, School, Users, FileText, CheckCircle, XCircle,
  Plus, Edit, Trash2, Eye, Search, Filter, RefreshCw,
  ChevronDown, ChevronUp, MapPin, Globe, Clock, AlertCircle,
  ArrowLeft, Award, TrendingUp, AlertTriangle, CloudRain,
  BookOpen, Calendar, Upload, Download, Save, X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db, isSupabaseConfigured } from '../lib/supabase';
import supabase from '../lib/supabase';

// Jamaica Parishes
const JAMAICA_PARISHES = [
  'Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary',
  'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland',
  'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'
];

// School levels
const SCHOOL_LEVELS = [
  { value: 'early_childhood', label: 'Early Childhood' },
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'tertiary', label: 'Tertiary' }
];

// School types
const SCHOOL_TYPES = [
  { value: 'traditional', label: 'Traditional High School' },
  { value: 'technical', label: 'Technical High School' },
  { value: 'upgraded', label: 'Upgraded High School' },
  { value: 'comprehensive', label: 'Comprehensive High School' },
  { value: 'agricultural', label: 'Agricultural High School' },
  { value: 'vocational', label: 'Vocational Training' },
  { value: 'all_age', label: 'All-Age School' },
  { value: 'primary_junior', label: 'Primary & Junior High' },
  { value: 'infant', label: 'Infant School' },
  { value: 'primary', label: 'Primary School' },
  { value: 'preparatory', label: 'Preparatory School' },
  { value: 'basic', label: 'Basic School' },
  { value: 'special', label: 'Special Education' }
];

// Ownership types
const OWNERSHIP_TYPES = [
  { value: 'public', label: 'Public (Government)' },
  { value: 'private', label: 'Private' },
  { value: 'grant_aided', label: 'Grant-Aided' },
  { value: 'church', label: 'Church-Operated' }
];

// Gender options
const GENDER_OPTIONS = [
  { value: 'coed', label: 'Co-Educational' },
  { value: 'boys', label: 'Boys Only' },
  { value: 'girls', label: 'Girls Only' }
];

// Listing tiers (for marketplace, does NOT affect rankings)
const LISTING_TIERS = [
  { value: 'free', label: 'Free' },
  { value: 'basic', label: 'Basic' },
  { value: 'premium', label: 'Premium' }
];

// Exam types
const EXAM_TYPES = [
  { value: 'PEP', label: 'PEP (Primary Exit Profile)' },
  { value: 'CSEC', label: 'CSEC (Caribbean Secondary Education Certificate)' },
  { value: 'CAPE', label: 'CAPE (Caribbean Advanced Proficiency Examination)' },
  { value: 'NVQJ', label: 'NVQ-J (National Vocational Qualification of Jamaica)' }
];

// ==================== ADMIN SCHOOLS PAGE ====================
export const AdminSchoolsPage = ({ setPage }) => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterParish, setFilterParish] = useState('');
  const [filterLevel, setFilterLevel] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    level: 'secondary',
    type: 'traditional',
    ownership: 'public',
    gender: 'coed',
    parish: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    principal_name: '',
    founded_year: '',
    motto: '',
    student_count: '',
    teacher_count: '',
    has_sixth_form: false,
    offers_boarding: false,
    sen_support: false,
    uniform_required: true,
    listing_tier: 'free',
    is_verified: false,
    latitude: '',
    longitude: ''
  });

  useEffect(() => {
    loadSchools();
  }, [filterParish, filterLevel]);

  const loadSchools = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (filterParish) filters.parish = filterParish;
      if (filterLevel) filters.level = filterLevel;

      const { data, error } = await db.schools.getAll(filters);
      if (error) throw error;
      setSchools(data || []);
    } catch (error) {
      console.error('Error loading schools:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue,
      ...(name === 'name' ? { slug: generateSlug(value) } : {})
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      level: 'secondary',
      type: 'traditional',
      ownership: 'public',
      gender: 'coed',
      parish: '',
      address: '',
      phone: '',
      email: '',
      website: '',
      principal_name: '',
      founded_year: '',
      motto: '',
      student_count: '',
      teacher_count: '',
      has_sixth_form: false,
      offers_boarding: false,
      sen_support: false,
      uniform_required: true,
      listing_tier: 'free',
      is_verified: false,
      latitude: '',
      longitude: ''
    });
    setEditingSchool(null);
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (school) => {
    setEditingSchool(school);
    setFormData({
      name: school.name || '',
      slug: school.slug || '',
      level: school.level || 'secondary',
      type: school.type || 'traditional',
      ownership: school.ownership || 'public',
      gender: school.gender || 'coed',
      parish: school.parish || '',
      address: school.address || '',
      phone: school.phone || '',
      email: school.email || '',
      website: school.website || '',
      principal_name: school.principal_name || '',
      founded_year: school.founded_year || '',
      motto: school.motto || '',
      student_count: school.student_count || '',
      teacher_count: school.teacher_count || '',
      has_sixth_form: school.has_sixth_form || false,
      offers_boarding: school.offers_boarding || false,
      sen_support: school.sen_support || false,
      uniform_required: school.uniform_required !== false,
      listing_tier: school.listing_tier || 'free',
      is_verified: school.is_verified || false,
      latitude: school.latitude || '',
      longitude: school.longitude || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const schoolData = {
        ...formData,
        student_count: formData.student_count ? parseInt(formData.student_count) : null,
        teacher_count: formData.teacher_count ? parseInt(formData.teacher_count) : null,
        founded_year: formData.founded_year ? parseInt(formData.founded_year) : null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null
      };

      if (editingSchool) {
        const { error } = await db.schools.update(editingSchool.id, schoolData);
        if (error) throw error;
      } else {
        const { error } = await db.schools.create(schoolData);
        if (error) throw error;
      }

      setShowModal(false);
      resetForm();
      loadSchools();
    } catch (error) {
      console.error('Error saving school:', error);
      alert('Error saving school: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (school) => {
    if (!window.confirm(`Delete ${school.name}? This action cannot be undone.`)) return;

    try {
      const { error } = await db.schools.delete(school.id);
      if (error) throw error;
      loadSchools();
    } catch (error) {
      console.error('Error deleting school:', error);
      alert('Error deleting school: ' + error.message);
    }
  };

  const filteredSchools = schools.filter(school =>
    school.name?.toLowerCase().includes(search.toLowerCase()) ||
    school.parish?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-600 to-brand-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <School className="w-8 h-8" />Manage Schools
            </h2>
            <p className="text-brand-100 mt-1">Add, edit, and manage school listings</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadSchools} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={openAddModal}
              className="flex items-center gap-2 px-4 py-2 bg-white text-brand-600 font-bold rounded-lg hover:bg-brand-50"
            >
              <Plus className="w-5 h-5" />Add School
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search schools..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
            />
          </div>
          <select
            value={filterParish}
            onChange={(e) => setFilterParish(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
          >
            <option value="">All Parishes</option>
            {JAMAICA_PARISHES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value)}
            className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
          >
            <option value="">All Levels</option>
            {SCHOOL_LEVELS.map(l => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </select>
          <div className="text-right text-gray-500 py-2">
            {filteredSchools.length} schools found
          </div>
        </div>
      </div>

      {/* Schools List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : filteredSchools.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Schools Found</h3>
          <p className="text-gray-400 mt-1">Add your first school to get started</p>
          <button
            onClick={openAddModal}
            className="mt-4 px-6 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700"
          >
            <Plus className="w-5 h-5 inline mr-2" />Add School
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-700">School</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700 hidden md:table-cell">Parish</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700 hidden md:table-cell">Level</th>
                <th className="text-left px-4 py-3 font-bold text-gray-700 hidden lg:table-cell">Type</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">Status</th>
                <th className="text-right px-4 py-3 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filteredSchools.map((school) => (
                <tr key={school.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-bold text-gray-900">{school.name}</p>
                      <p className="text-sm text-gray-500">{school.principal_name || 'Principal not set'}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-gray-600">{school.parish}</span>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-sm capitalize">
                      {school.level?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <span className="text-gray-600 capitalize">{school.type?.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {school.is_verified ? (
                      <span className="px-2 py-1 bg-brand-100 text-brand-700 rounded-full text-sm flex items-center justify-center gap-1">
                        <CheckCircle className="w-4 h-4" />Verified
                      </span>
                    ) : (
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                        Unverified
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setPage(`school-${school.slug}`)}
                        className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                        title="View"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => openEditModal(school)}
                        className="p-2 text-gray-500 hover:text-brand-600 hover:bg-brand-50 rounded-lg"
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(school)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-black flex items-center gap-2">
                <School className="w-6 h-6 text-brand-600" />
                {editingSchool ? 'Edit School' : 'Add New School'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-6">
              {/* Basic Info */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />Basic Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">URL Slug</label>
                    <input
                      type="text"
                      name="slug"
                      value={formData.slug}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none bg-gray-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Founded Year</label>
                    <input
                      type="number"
                      name="founded_year"
                      value={formData.founded_year}
                      onChange={handleInputChange}
                      min="1600"
                      max="2026"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Motto</label>
                    <input
                      type="text"
                      name="motto"
                      value={formData.motto}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Classification */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Filter className="w-5 h-5" />Classification
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Level *</label>
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                      required
                    >
                      {SCHOOL_LEVELS.map(l => (
                        <option key={l.value} value={l.value}>{l.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    >
                      {SCHOOL_TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Ownership</label>
                    <select
                      name="ownership"
                      value={formData.ownership}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    >
                      {OWNERSHIP_TYPES.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                    <select
                      name="gender"
                      value={formData.gender}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    >
                      {GENDER_OPTIONS.map(g => (
                        <option key={g.value} value={g.value}>{g.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Location */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5" />Location
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Parish *</label>
                    <select
                      name="parish"
                      value={formData.parish}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                      required
                    >
                      <option value="">Select Parish</option>
                      {JAMAICA_PARISHES.map(p => (
                        <option key={p} value={p}>{p}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Latitude</label>
                    <input
                      type="number"
                      name="latitude"
                      value={formData.latitude}
                      onChange={handleInputChange}
                      step="any"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Longitude</label>
                    <input
                      type="number"
                      name="longitude"
                      value={formData.longitude}
                      onChange={handleInputChange}
                      step="any"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Globe className="w-5 h-5" />Contact Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Principal Name</label>
                    <input
                      type="text"
                      name="principal_name"
                      value={formData.principal_name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
                    <input
                      type="url"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Statistics */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">
                  <Users className="w-5 h-5" />Statistics
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Student Count</label>
                    <input
                      type="number"
                      name="student_count"
                      value={formData.student_count}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teacher Count</label>
                    <input
                      type="number"
                      name="teacher_count"
                      value={formData.teacher_count}
                      onChange={handleInputChange}
                      min="0"
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    />
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <h4 className="font-bold text-gray-700 mb-3">Features & Options</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="has_sixth_form"
                      checked={formData.has_sixth_form}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm">Has Sixth Form</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="offers_boarding"
                      checked={formData.offers_boarding}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm">Offers Boarding</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="sen_support"
                      checked={formData.sen_support}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm">SEN Support</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="uniform_required"
                      checked={formData.uniform_required}
                      onChange={handleInputChange}
                      className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-sm">Uniform Required</span>
                  </label>
                </div>
              </div>

              {/* Admin Settings */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-bold text-gray-700 mb-3">Admin Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Listing Tier</label>
                    <select
                      name="listing_tier"
                      value={formData.listing_tier}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-brand-500 focus:outline-none"
                    >
                      {LISTING_TIERS.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Note: Tier affects marketplace features only, NOT rankings</p>
                  </div>
                  <div className="flex items-center">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="is_verified"
                        checked={formData.is_verified}
                        onChange={handleInputChange}
                        className="w-5 h-5 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span className="font-medium">Verified School</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-brand-600 text-white font-bold rounded-xl hover:bg-brand-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-5 h-5" />{editingSchool ? 'Update School' : 'Add School'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN EXAM RESULTS PAGE ====================
export const AdminExamResultsPage = ({ setPage }) => {
  const [schools, setSchools] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSchool, setSelectedSchool] = useState('');
  const [selectedExamType, setSelectedExamType] = useState('CSEC');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state for adding results
  const [formData, setFormData] = useState({
    school_id: '',
    exam_type: 'CSEC',
    year: new Date().getFullYear(),
    subject: '',
    students_sat: '',
    grade_1: '',
    grade_2: '',
    grade_3: '',
    grade_4: '',
    grade_5: '',
    ungraded: ''
  });

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    if (selectedSchool) {
      loadResults();
    }
  }, [selectedSchool, selectedExamType, selectedYear]);

  const loadSchools = async () => {
    try {
      const { data } = await db.schools.getAll({ level: 'secondary' });
      setSchools(data || []);
    } catch (error) {
      console.error('Error loading schools:', error);
    }
  };

  const loadResults = async () => {
    setLoading(true);
    try {
      const { data } = await db.examResults.getBySchool(selectedSchool);
      // Filter by exam type and year
      const filtered = (data || []).filter(r =>
        r.exam_type === selectedExamType && r.year === selectedYear
      );
      setResults(filtered);
    } catch (error) {
      console.error('Error loading results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const calculatePassRate = () => {
    const grades1to3 = (parseInt(formData.grade_1) || 0) +
                       (parseInt(formData.grade_2) || 0) +
                       (parseInt(formData.grade_3) || 0);
    const total = parseInt(formData.students_sat) || 0;
    if (total === 0) return 0;
    return ((grades1to3 / total) * 100).toFixed(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const students_sat = parseInt(formData.students_sat) || 0;
      const grade_1 = parseInt(formData.grade_1) || 0;
      const grade_2 = parseInt(formData.grade_2) || 0;
      const grade_3 = parseInt(formData.grade_3) || 0;
      const grade_4 = parseInt(formData.grade_4) || 0;
      const grade_5 = parseInt(formData.grade_5) || 0;
      const ungraded = parseInt(formData.ungraded) || 0;

      const pass_rate = students_sat > 0
        ? ((grade_1 + grade_2 + grade_3) / students_sat) * 100
        : 0;

      const resultData = {
        school_id: formData.school_id,
        exam_type: formData.exam_type,
        year: parseInt(formData.year),
        subject: formData.subject,
        students_sat,
        grades_achieved: {
          grade_1,
          grade_2,
          grade_3,
          grade_4,
          grade_5,
          ungraded
        },
        pass_rate: parseFloat(pass_rate.toFixed(2))
      };

      const { error } = await supabase.from('exam_results').insert(resultData);
      if (error) throw error;

      setShowModal(false);
      setFormData({
        school_id: selectedSchool,
        exam_type: selectedExamType,
        year: selectedYear,
        subject: '',
        students_sat: '',
        grade_1: '',
        grade_2: '',
        grade_3: '',
        grade_4: '',
        grade_5: '',
        ungraded: ''
      });
      loadResults();
    } catch (error) {
      console.error('Error saving result:', error);
      alert('Error saving result: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (resultId) => {
    if (!window.confirm('Delete this exam result?')) return;
    try {
      const { error } = await supabase.from('exam_results').delete().eq('id', resultId);
      if (error) throw error;
      loadResults();
    } catch (error) {
      console.error('Error deleting result:', error);
    }
  };

  const openAddModal = () => {
    setFormData({
      school_id: selectedSchool,
      exam_type: selectedExamType,
      year: selectedYear,
      subject: '',
      students_sat: '',
      grade_1: '',
      grade_2: '',
      grade_3: '',
      grade_4: '',
      grade_5: '',
      ungraded: ''
    });
    setShowModal(true);
  };

  const years = [];
  for (let y = new Date().getFullYear(); y >= 2015; y--) {
    years.push(y);
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Award className="w-8 h-8" />Exam Results Management
            </h2>
            <p className="text-blue-100 mt-1">Input and manage CSEC, CAPE, PEP results</p>
          </div>
          <button onClick={loadResults} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">School</label>
            <select
              value={selectedSchool}
              onChange={(e) => setSelectedSchool(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            >
              <option value="">Select School</option>
              {schools.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Exam Type</label>
            <select
              value={selectedExamType}
              onChange={(e) => setSelectedExamType(e.target.value)}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            >
              {EXAM_TYPES.map(e => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
            >
              {years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={openAddModal}
              disabled={!selectedSchool}
              className="w-full px-4 py-2 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Plus className="w-5 h-5" />Add Result
            </button>
          </div>
        </div>
      </div>

      {/* Results Table */}
      {!selectedSchool ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <School className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">Select a School</h3>
          <p className="text-gray-400 mt-1">Choose a school to view and manage exam results</p>
        </div>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : results.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Results Found</h3>
          <p className="text-gray-400 mt-1">No {selectedExamType} results for {selectedYear}</p>
          <button
            onClick={openAddModal}
            className="mt-4 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700"
          >
            <Plus className="w-5 h-5 inline mr-2" />Add First Result
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 font-bold text-gray-700">Subject</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">Sat</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">G1</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">G2</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">G3</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">G4</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">G5</th>
                <th className="text-center px-4 py-3 font-bold text-gray-700">Pass %</th>
                <th className="text-right px-4 py-3 font-bold text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {results.map((result) => (
                <tr key={result.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium">{result.subject}</td>
                  <td className="px-4 py-3 text-center">{result.students_sat}</td>
                  <td className="px-4 py-3 text-center">{result.grades_achieved?.grade_1 || 0}</td>
                  <td className="px-4 py-3 text-center">{result.grades_achieved?.grade_2 || 0}</td>
                  <td className="px-4 py-3 text-center">{result.grades_achieved?.grade_3 || 0}</td>
                  <td className="px-4 py-3 text-center">{result.grades_achieved?.grade_4 || 0}</td>
                  <td className="px-4 py-3 text-center">{result.grades_achieved?.grade_5 || 0}</td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-1 rounded-full text-sm font-bold ${
                      result.pass_rate >= 80 ? 'bg-brand-100 text-brand-700' :
                      result.pass_rate >= 60 ? 'bg-blue-100 text-blue-700' :
                      result.pass_rate >= 40 ? 'bg-amber-100 text-amber-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {result.pass_rate?.toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => handleDelete(result.id)}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Result Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-black flex items-center gap-2">
                <Award className="w-6 h-6 text-blue-600" />
                Add Exam Result
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  placeholder="e.g., Mathematics, English A, Biology"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Students Sat *</label>
                <input
                  type="number"
                  name="students_sat"
                  value={formData.students_sat}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade 1</label>
                  <input
                    type="number"
                    name="grade_1"
                    value={formData.grade_1}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade 2</label>
                  <input
                    type="number"
                    name="grade_2"
                    value={formData.grade_2}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade 3</label>
                  <input
                    type="number"
                    name="grade_3"
                    value={formData.grade_3}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade 4</label>
                  <input
                    type="number"
                    name="grade_4"
                    value={formData.grade_4}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Grade 5</label>
                  <input
                    type="number"
                    name="grade_5"
                    value={formData.grade_5}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ungraded</label>
                  <input
                    type="number"
                    name="ungraded"
                    value={formData.ungraded}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none"
                  />
                </div>
              </div>

              {formData.students_sat && (
                <div className="bg-blue-50 rounded-xl p-4 text-center">
                  <p className="text-sm text-blue-600">Calculated Pass Rate (Grades 1-3)</p>
                  <p className="text-3xl font-black text-blue-700">{calculatePassRate()}%</p>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-5 h-5" />Save Result</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN DISRUPTIONS PAGE ====================
export const AdminDisruptionsPage = ({ setPage }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    type: 'hurricane',
    start_date: '',
    end_date: '',
    affected_parishes: [],
    description: ''
  });

  const DISRUPTION_TYPES = [
    { value: 'hurricane', label: 'Hurricane', icon: CloudRain },
    { value: 'flood', label: 'Flood', icon: CloudRain },
    { value: 'pandemic', label: 'Pandemic', icon: AlertTriangle },
    { value: 'earthquake', label: 'Earthquake', icon: AlertTriangle },
    { value: 'civil_unrest', label: 'Civil Unrest', icon: AlertCircle },
    { value: 'other', label: 'Other', icon: AlertCircle }
  ];

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('disruption_events')
        .select('*')
        .order('start_date', { ascending: false });
      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleParishToggle = (parish) => {
    setFormData(prev => ({
      ...prev,
      affected_parishes: prev.affected_parishes.includes(parish)
        ? prev.affected_parishes.filter(p => p !== parish)
        : [...prev.affected_parishes, parish]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const { error } = await supabase.from('disruption_events').insert({
        ...formData,
        affected_parishes: formData.affected_parishes
      });
      if (error) throw error;

      setShowModal(false);
      setFormData({
        name: '',
        type: 'hurricane',
        start_date: '',
        end_date: '',
        affected_parishes: [],
        description: ''
      });
      loadEvents();
    } catch (error) {
      console.error('Error saving event:', error);
      alert('Error saving event: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this disruption event?')) return;
    try {
      const { error } = await supabase.from('disruption_events').delete().eq('id', eventId);
      if (error) throw error;
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />Disruption Events
            </h2>
            <p className="text-amber-100 mt-1">Manage hurricanes, floods, and other events affecting schools</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={loadEvents} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
              <RefreshCw className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-amber-600 font-bold rounded-lg hover:bg-amber-50"
            >
              <Plus className="w-5 h-5" />Add Event
            </button>
          </div>
        </div>
      </div>

      {/* Events List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : events.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <CloudRain className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Disruption Events</h3>
          <p className="text-gray-400 mt-1">Add events that affected school operations</p>
        </div>
      ) : (
        <div className="space-y-4">
          {events.map((event) => (
            <div key={event.id} className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    event.type === 'hurricane' ? 'bg-blue-100 text-blue-600' :
                    event.type === 'pandemic' ? 'bg-purple-100 text-purple-600' :
                    'bg-amber-100 text-amber-600'
                  }`}>
                    <CloudRain className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">{event.name}</h3>
                    <p className="text-gray-500 capitalize">{event.type.replace('_', ' ')}</p>
                    <p className="text-sm text-gray-400 mt-1">
                      {new Date(event.start_date).toLocaleDateString()}
                      {event.end_date && ` - ${new Date(event.end_date).toLocaleDateString()}`}
                    </p>
                    {event.affected_parishes?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {event.affected_parishes.map(p => (
                          <span key={p} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                            {p}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => handleDelete(event.id)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h3 className="text-xl font-black flex items-center gap-2">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
                Add Disruption Event
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Event Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="e.g., Hurricane Beryl 2024"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                >
                  {DISRUPTION_TYPES.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Affected Parishes</label>
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto p-2 border-2 border-gray-200 rounded-xl">
                  {JAMAICA_PARISHES.map(parish => (
                    <label key={parish} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.affected_parishes.includes(parish)}
                        onChange={() => handleParishToggle(parish)}
                        className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                      />
                      <span className="text-sm">{parish}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
                  placeholder="Brief description of the event and its impact..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 font-bold rounded-xl hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-6 py-3 bg-amber-500 text-white font-bold rounded-xl hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <><RefreshCw className="w-5 h-5 animate-spin" />Saving...</>
                  ) : (
                    <><Save className="w-5 h-5" />Save Event</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// ==================== ADMIN DATA SUGGESTIONS PAGE ====================
export const AdminSuggestionsPage = ({ setPage }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');

  useEffect(() => {
    loadSuggestions();
  }, [filter]);

  const loadSuggestions = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('school_data_suggestions')
        .select(`
          *,
          schools (name)
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;
      if (error) throw error;
      setSuggestions(data || []);
    } catch (error) {
      console.error('Error loading suggestions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (suggestion) => {
    if (!window.confirm('Approve and apply this suggestion?')) return;

    try {
      // Update the suggestion status
      const { error } = await supabase
        .from('school_data_suggestions')
        .update({
          status: 'approved',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', suggestion.id);

      if (error) throw error;

      // Apply the suggested data to the school
      if (suggestion.school_id && suggestion.suggested_data) {
        const { error: updateError } = await supabase
          .from('schools')
          .update(suggestion.suggested_data)
          .eq('id', suggestion.school_id);

        if (updateError) throw updateError;
      }

      loadSuggestions();
    } catch (error) {
      console.error('Error approving suggestion:', error);
      alert('Error: ' + error.message);
    }
  };

  const handleReject = async (suggestionId) => {
    if (!window.confirm('Reject this suggestion?')) return;

    try {
      const { error } = await supabase
        .from('school_data_suggestions')
        .update({
          status: 'rejected',
          reviewed_at: new Date().toISOString()
        })
        .eq('id', suggestionId);

      if (error) throw error;
      loadSuggestions();
    } catch (error) {
      console.error('Error rejecting suggestion:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-brand-100 text-brand-700';
      case 'rejected': return 'bg-red-100 text-red-700';
      default: return 'bg-amber-100 text-amber-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-black flex items-center gap-3">
              <Edit className="w-8 h-8" />Data Suggestions
            </h2>
            <p className="text-purple-100 mt-1">Review community-submitted corrections</p>
          </div>
          <button onClick={loadSuggestions} className="p-2 bg-white/20 rounded-lg hover:bg-white/30">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 rounded-xl font-medium capitalize ${
              filter === f ? 'bg-purple-600 text-white' : 'bg-white text-gray-700 border'
            }`}
          >
            {f}
          </button>
        ))}
      </div>

      {/* Suggestions List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
        </div>
      ) : suggestions.length === 0 ? (
        <div className="bg-gray-50 rounded-2xl p-12 text-center">
          <Edit className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-500">No Suggestions</h3>
          <p className="text-gray-400 mt-1">No {filter !== 'all' && filter} suggestions found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <div key={suggestion.id} className="bg-white rounded-xl shadow-lg p-5">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg">{suggestion.schools?.name || 'Unknown School'}</h3>
                  <p className="text-gray-500">{suggestion.field_name}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(suggestion.status)}`}>
                    {suggestion.status}
                  </span>
                </div>
                <p className="text-xs text-gray-400">
                  {new Date(suggestion.created_at).toLocaleDateString()}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Current Value</p>
                  <p className="font-medium text-red-600">
                    {JSON.stringify(suggestion.current_value) || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 uppercase mb-1">Suggested Value</p>
                  <p className="font-medium text-brand-600">
                    {JSON.stringify(suggestion.suggested_value) || 'N/A'}
                  </p>
                </div>
              </div>

              {suggestion.submitter_email && (
                <p className="text-sm text-gray-500 mt-3">
                  Submitted by: {suggestion.submitter_email}
                </p>
              )}

              {suggestion.status === 'pending' && (
                <div className="flex gap-3 mt-4 pt-4 border-t">
                  <button
                    onClick={() => handleApprove(suggestion)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white font-bold rounded-xl"
                  >
                    <CheckCircle className="w-5 h-5" />Approve & Apply
                  </button>
                  <button
                    onClick={() => handleReject(suggestion.id)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl"
                  >
                    <XCircle className="w-5 h-5" />Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button onClick={() => setPage('admin')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
        <ArrowLeft className="w-5 h-5" />Back to Dashboard
      </button>
    </div>
  );
};

// Export all components
export default {
  AdminSchoolsPage,
  AdminExamResultsPage,
  AdminDisruptionsPage,
  AdminSuggestionsPage
};
